import create from 'zustand';
import axios from 'axios';
import api2 from '~/services/api2';
import { toast } from 'react-hot-toast';
import React from 'react';

const whatsappApi = axios.create({
  baseURL: process.env.REACT_APP_TAKEAT_WHATSAPP_API_BASE_URL,
  headers: {
    apiKey: process.env.REACT_APP_TAKEAT_WHATSAPP_API_TOKEN,
  },
});

const normalizeQrCodeToDataUrl = (qrCode) => {
  if (typeof qrCode === 'string') {
    if (/^(data:|blob:|https?:)/.test(qrCode)) return qrCode;
    return `data:image/png;base64,${qrCode}`;
  }

  const base64 = qrCode?.base64;
  if (typeof base64 === 'string' && base64.trim() !== '') {
    if (/^data:/.test(base64)) return base64;
    return `data:image/png;base64,${base64}`;
  }
  return null;
};

// Normalize incoming webhook/API statuses to the canonical set:
// 'created' | 'open' | 'close' | 'unknown'
function normalizeWebhookStatus(raw) {
  if (raw === null || raw === undefined) return 'unknown';
  const s = String(raw).toLowerCase();

  if (s === 'open' || s === 'connected') return 'open';
  if (s === 'created' || s === 'connecting') return 'created';

  if (
    s === 'close' ||
    s === 'closed' ||
    s === 'disconnected' ||
    s === 'disconnect'
  ) {
    return 'close';
  }

  return 'unknown';
}

// @ts-nocheck

function getInitialWppStatus() {
  try {
    const stored = localStorage.getItem('@gddashboard:wppStatus');
    const parsed = stored ? JSON.parse(stored) : null;
    if (
      parsed === 'open' ||
      parsed === 'close' ||
      parsed === 'created' ||
      parsed === 'connecting' ||
      parsed === 'unknown'
    )
      return parsed;
  } catch (e) {
    // ignore
  }
  return 'unknown';
}
function getInitialAgentEnabled() {
  return false;
}

const useWhatsappStore = create((set, get) => ({
  // States
  useTakeatWpp: null,
  wppConnected: null,
  // Tri-state connection status: 'open' | 'close' | 'created' | 'unknown'
  wppStatus: getInitialWppStatus(),
  wppQrcode: null,
  loadingQrCode: false,
  showReconnect: false,
  wppFailedMessage: null,
  // Indicates whether we already performed an API status check
  wppStatusChecked: false,
  wppApiAvailable: false,
  lastStatusSource: null,
  waitingForWebhook: false,
  _webhookTimeoutId: null,
  // AI chatbot enabled state
  isAgentEnabled: getInitialAgentEnabled(),
  isAgentLoading: false,
  // Plan type for WhatsApp integration
  planType: null,
  phoneDetails: {
    instance_name: null,
    phone_number: null,
    created_at: null,
    status: null,
  },
  // Setters
  setShowReconnect: (value) => set({ showReconnect: value }),
  setWppFailedMessage: (value) => set({ wppFailedMessage: value }),
  setWppQrcode: (value) => set({ wppQrcode: value }),
  setLoadingQrCode: (value) => set({ loadingQrCode: value }),
  setWaitingForWebhook: (value) => set({ waitingForWebhook: value }),
  setWppStatus: (status) => {
    const normalized = normalizeWebhookStatus(status);
    try {
      localStorage.setItem(
        '@gddashboard:wppStatus',
        JSON.stringify(normalized)
      );
    } catch (e) {
      // ignore
    }
    set({ wppStatus: normalized });
  },
  updateStatusFromWebsocket: (connected) => {
    // Accept boolean (legacy) or string tri-state and normalize
    let nextStatus = 'unknown';
    if (typeof connected === 'boolean')
      nextStatus = connected ? 'open' : 'close';
    else nextStatus = normalizeWebhookStatus(connected);

    try {
      localStorage.setItem(
        '@gddashboard:wppConnected',
        JSON.stringify(nextStatus === 'open')
      );
      localStorage.setItem(
        '@gddashboard:wppStatus',
        JSON.stringify(nextStatus)
      );
    } catch (e) {
      // ignore
    }

    set({
      wppConnected: nextStatus === 'open',
      wppStatus: nextStatus,
      lastStatusSource: 'websocket',
      showReconnect: nextStatus === 'open' ? false : get().showReconnect,
      phoneDetails:
        nextStatus === 'open'
          ? get().phoneDetails
          : {
              instance_name: null,
              phone_number: null,
              created_at: null,
              status: null,
            },
    });

    // After updating from websocket, query the instance to refresh full context
    try {
      const query = get()?.queryInstance;
      if (typeof query === 'function') query();
    } catch (e) {
      // ignore
    }
  },
  // Helpers (normalize/parse/update)
  parseConnectionResponse: (data) => {
    try {
      return (
        data.result === 'success' &&
        (data.phone_state === 'open' ||
          data.phone_state === 'OPEN' ||
          data.phone_state === 'connected' ||
          data.phone_state === 'CONNECTED')
      );
    } catch (e) {
      return false;
    }
  },
  isExplicitlyDisconnectedState: (data) => {
    const state = data?.phone_state;
    if (!state || typeof state !== 'string') return false;
    const normalized = state.toLowerCase();
    return (
      normalized === 'created' ||
      normalized === 'closed' ||
      normalized === 'disconnect' ||
      normalized === 'disconnected'
    );
  },
  updateConnectionStatus: (connected, source = 'api') => {
    try {
      // store connection and source
      if (connected === null) {
        localStorage.setItem('@gddashboard:wppConnected', JSON.stringify(null));
      } else {
        localStorage.setItem(
          '@gddashboard:wppConnected',
          JSON.stringify(connected)
        );
      }

      const statusFromBool =
        connected === null ? 'unknown' : connected ? 'open' : 'close';
      try {
        localStorage.setItem(
          '@gddashboard:wppStatus',
          JSON.stringify(statusFromBool)
        );
      } catch (e) {
        // ignore
      }

      set({
        wppConnected: connected,
        wppStatus: statusFromBool,
        lastStatusSource: source,
      });
    } catch (e) {
      // ignore storage errors
      set({
        wppConnected: connected,
        wppStatus: connected ? 'open' : 'close',
        lastStatusSource: source,
      });
    }
  },
  // Actions

  // New method to check instance status from phone-state endpoint - only use this to check if an instance exists
  fetchInstanceStatus: async () => {
    try {
      const { data } = await api2.get('/restaurants/whatsgw/status');
      return { success: true, data };
    } catch (err) {
      // Handle the specific error case for no instance
      if (
        err?.response?.status === 400 &&
        err?.response?.data?.message
          ?.toLowerCase()
          .includes('no current instance')
      ) {
        set({
          wppConnected: false,
          wppStatus: 'close',
          wppStatusChecked: true,
          phoneDetails: {
            instance_name: null,
            phone_number: null,
            created_at: null,
            status: null,
          },
        });

        try {
          localStorage.setItem(
            '@gddashboard:wppStatus',
            JSON.stringify('close')
          );
          localStorage.removeItem('@gddashboard:wppConnected');
        } catch (e) {
          /* ignore */
        }

        return {
          success: false,
          error: err?.response?.data || {
            message: 'Restaurant has no current instance',
          },
          noInstance: true,
        };
      }

      // For any other error
      console.error('Failed to fetch instance status', err);
      set({
        wppConnected: false,
        wppStatus: 'unknown',
        wppApiAvailable: false,
        phoneDetails: {
          instance_name: null,
          phone_number: null,
          created_at: null,
          status: null,
        },
      });
      return { success: false, error: err?.response?.data };
    }
  },

  // Fetch restaurant info and current WhatsApp instance details
  fetchRestaurantInfo: async () => {
    const userStr = localStorage.getItem('@gddashboard:user');
    if (!userStr) {
      console.error('fetchRestaurantInfo: User not found in localStorage');
      return { success: false, error: 'User not found' };
    }

    const user = JSON.parse(userStr);
    const restaurantSourceId = user.id;

    if (!restaurantSourceId) {
      console.error(
        'fetchRestaurantInfo: restaurantSourceId (user.id) not found in user object',
        user
      );
      return { success: false, error: 'Restaurant ID not found' };
    }

    try {
      const { data: restaurant } = await whatsappApi.get(
        `/restaurant/source/${restaurantSourceId}`
      );

      const instance = restaurant?.currentInstance;
      const planType = restaurant?.planType || null;
      const isAgentEnabled =
        restaurant?.isAgentEnabled || restaurant?.chatbotEnabled || false;

      // Set planType and isAgentEnabled regardless of instance presence
      set({ planType, isAgentEnabled });

      if (!instance) {
        try {
          localStorage.setItem(
            '@gddashboard:wppStatus',
            JSON.stringify('close')
          );
          localStorage.setItem(
            '@gddashboard:wppConnected',
            JSON.stringify(false)
          );
        } catch (e) {
          /* ignore */
        }

        set({
          wppConnected: false,
          wppStatus: 'close',
          phoneDetails: {
            instance_name: null,
            phone_number: null,
            created_at: null,
            status: null,
          },
        });

        return {
          success: true,
          data: { restaurant, instance: null, planType },
        };
      }

      // Instance exists - normalize status and update state
      const mappedStatus = normalizeWebhookStatus(instance.status);

      try {
        localStorage.setItem(
          '@gddashboard:wppStatus',
          JSON.stringify(mappedStatus)
        );
        localStorage.setItem(
          '@gddashboard:wppConnected',
          JSON.stringify(mappedStatus === 'open')
        );
      } catch (e) {
        /* ignore */
      }

      set({
        wppConnected: mappedStatus === 'open',
        wppStatus: mappedStatus,
        planType,
        wppApiAvailable: true,
        wppStatusChecked: true,
        phoneDetails: {
          instance_name: instance.instanceName,
          phone_number: instance.phoneNumber,
          created_at: instance.createdAt,
          status: mappedStatus,
        },
      });

      return {
        success: true,
        data: { restaurant, instance, planType },
      };
    } catch (error) {
      console.error('Failed to fetch restaurant info', error);
      set({
        wppConnected: false,
        wppStatus: 'unknown',
        wppApiAvailable: false,
        phoneDetails: {
          instance_name: null,
          phone_number: null,
          created_at: null,
          status: null,
        },
      });
      return { success: false, error };
    }
  },

  // Wrapper for backward compatibility
  queryInstance: async () => {
    const result = await get().fetchRestaurantInfo();
    return result.success ? result.data : null;
  },

  logoutWpp: async () => {
    // clear any pending webhook timeout when logging out
    try {
      const timeoutId = get()?._webhookTimeoutId;
      if (timeoutId) clearTimeout(timeoutId);
    } catch (e) {
      // ignore
    }
    set({ loadingQrCode: true });
    try {
      await api2.post('/restaurants/whatsgw/logout');

      set({
        wppConnected: false,
        wppStatus: 'close',
        wppQrcode: null,
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          'Erro ao desconectar WhatsApp. Tente novamente'
      );
    }
    set({ loadingQrCode: false });
  },

  createInstance: async (invalidateOldQrcode = false) => {
    set({ loadingQrCode: true });
    // Enter connecting state while creating/awaiting QR
    try {
      localStorage.setItem('@gddashboard:wppStatus', JSON.stringify('created'));
    } catch (e) {
      /* ignore */
    }
    set({ wppStatus: 'created' });
    if (invalidateOldQrcode) {
      set({ wppQrcode: null });
    }

    try {
      const userStrLocal = localStorage.getItem('@gddashboard:user');
      let isAlreadyConnected = false;
      if (userStrLocal) {
        try {
          const userLocal = JSON.parse(userStrLocal);
          const restaurantSourceIdLocal = userLocal?.id;
          if (restaurantSourceIdLocal) {
            const { data: restaurantLocal } = await whatsappApi.get(
              `/restaurant/source/${restaurantSourceIdLocal}`
            );
            const instanceLocal = restaurantLocal?.currentInstance;
            isAlreadyConnected = !!(
              instanceLocal && instanceLocal.status === 'open'
            );
          }
        } catch (e) {
          // ignore JSON parse or network errors here — fallback to not connected
          isAlreadyConnected = false;
        }
      }

      if (isAlreadyConnected) {
        toast.error(
          'Para gerar um novo qrcode, desconecte o dispositivo já conectado.'
        );
        set({ loadingQrCode: false });
        return null;
      }

      const response = await api2.post('/restaurants/whatsgw/createInstance');

      // Update phoneDetails with instance information from response
      if (response.data) {
        set({
          phoneDetails: {
            instance_name: response.data.instance_name || null,
            phone_number: response.data.phone_number || null,
            created_at: response.data.created_at || null,
            status: 'created',
          },
        });
      }

      // If backend returned a qrcode object but its base64 is an empty string,
      // consider that no QR code was generated and re-enable the create button.
      const receivedQrcode = response?.data?.qrcode;
      if (
        receivedQrcode &&
        typeof receivedQrcode.base64 === 'string' &&
        receivedQrcode.base64.trim() === ''
      ) {
        set({
          loadingQrCode: false,
          wppQrcode: null,
          waitingForWebhook: false,
        });
        return response.data;
      }

      const qrCode = normalizeQrCodeToDataUrl(response.data?.qrcode);

      if (qrCode) {
        set({
          wppQrcode: qrCode,
          loadingQrCode: false,
          waitingForWebhook: false,
          wppStatus: 'created',
        });
      } else if (response.data?.wait_webhook) {
        // If the backend signals we should wait for a webhook, we set waiting state
        set({ waitingForWebhook: true, wppStatus: 'created' }); // loading is still true

        // Start a 1-minute timeout that will cancel the waiting state if no webhook arrives
        const TIMEOUT_MS = 60 * 1000;
        const timeoutId = setTimeout(() => {
          const stillWaiting = get()?.waitingForWebhook;
          if (stillWaiting) {
            set({
              loadingQrCode: false,
              waitingForWebhook: false,
              wppStatus: 'close',
            });
            toast.error(
              'Tempo esgotado ao aguardar o QR Code. Tente novamente.'
            );
          }
        }, TIMEOUT_MS);

        // Store the timeout id in state so other actions (like logout) can clear it if needed
        set({ _webhookTimeoutId: timeoutId });
      } else {
        toast.error(
          'Não foi possível fazer o login no whatsapp. Tente novamente em alguns segundos.'
        );
        set({ loadingQrCode: false, wppStatus: 'close' });
      }
      return response.data;
    } catch (err) {
      toast.error('Erro ao carregar QRCode, clique para gerar um novamente');
      set({
        loadingQrCode: false,
        waitingForWebhook: false,
        wppStatus: 'close',
      });
      return null;
    }
  },

  // Wrapper for backward compatibility
  // Now this just checks instance status and fallbacks to legacy API if needed
  checkWppStatus: async () => {
    // First check if instance exists using the new endpoint
    const instanceStatusResult = await get().fetchInstanceStatus();

    // If we got a clear "no instance" response, we already updated the state in fetchInstanceStatus
    if (!instanceStatusResult.success && instanceStatusResult.noInstance) {
      set({ wppStatusChecked: true });
      return false;
    }

    // If instance status check was successful, fetch full restaurant info
    if (instanceStatusResult.success) {
      const restaurantInfoResult = await get().fetchRestaurantInfo();
      if (restaurantInfoResult.success) {
        const instance = restaurantInfoResult.data?.instance;
        return instance?.status === 'open';
      }
    }

    // Fallback to legacy API for any other case
    try {
      const response = await api2.get('/restaurants/whatsgw/status');
      set({ wppApiAvailable: true, wppStatusChecked: true });

      const connected = get().parseConnectionResponse(response.data);
      const explicitlyDisconnected = get().isExplicitlyDisconnectedState(
        response.data
      );

      // Determine status
      let triStatus = 'unknown';
      if (connected) triStatus = 'open';
      else if (explicitlyDisconnected) triStatus = 'close';
      else
        triStatus = normalizeWebhookStatus(
          response?.data?.phone_state || response?.data?.status || ''
        );

      // Update phone details if connected
      if (connected && response.data) {
        set({
          phoneDetails: {
            instance_name: response.data.instance_name || null,
            phone_number: response.data.phone_number || null,
            created_at: response.data.created_at || null,
            status: triStatus,
          },
        });
      }

      // Update connection status
      get().updateConnectionStatus(connected, 'api');
      set({ wppStatus: triStatus });

      return connected;
    } catch (err) {
      // Handle API error
      console.error('Legacy status check error >', err);
      set({ wppApiAvailable: true, wppStatusChecked: true });
      get().updateConnectionStatus(false, 'api');
      return false;
    }
  },

  // Called by webhook to set QR code
  updateQrCodeFromWebhook: (qrCodeData) => {
    const normalized = normalizeQrCodeToDataUrl(qrCodeData);
    if (normalized) {
      set({
        wppQrcode: normalized,
        loadingQrCode: false,
        waitingForWebhook: false,
        wppStatus: 'created',
      });
    }
  },

  // Messaging helpers
  sendMessage: async (payload) => {
    return api2.post('/whatsapp/send', payload);
  },
  listMessages: async () => {
    const { data } = await api2.get('/whatsapp/messages');
    return data;
  },
  // Clear pending webhook timeout if present
  clearWebhookTimeout: () => {
    const timeoutId = get()?._webhookTimeoutId;
    if (timeoutId) {
      try {
        clearTimeout(timeoutId);
      } catch (e) {
        // ignore
      }
      set({ _webhookTimeoutId: null });
    }
  },

  // Toggle AI chatbot
  toggleAgent: async () => {
    const userStr = localStorage.getItem('@gddashboard:user');
    if (!userStr) {
      console.error('toggleAgent: User not found in localStorage');
      return;
    }

    const user = JSON.parse(userStr);
    const restaurantId = user.id;

    if (!restaurantId) {
      console.error(
        'toggleAgent: restaurantId (user.id) not found in user object',
        user
      );
      return;
    }

    set({ isAgentLoading: true });
    try {
      // Toggle the current state
      const currentState = get().isAgentEnabled;
      const nextState = !currentState;

      await whatsappApi.put(
        `/restaurant/chatbot/toggle/source/${restaurantId}`,
        {
          enabled: nextState,
        }
      );

      // Update local state
      set({ isAgentEnabled: nextState, isAgentLoading: false });
    } catch (error) {
      console.error('Failed to toggle AI agent', error);
      toast.error('Erro ao alterar o status do assistente de IA');
      set({ isAgentLoading: false });
    }
  },
}));

export default useWhatsappStore;
